'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { BUILD_VERSION, fetchServedVersion, isStale } from './buildVersion';

/**
 * VERSION_POLL_MS — how often to re-check while the tab is visible.
 *
 * 30 minutes, not seconds: the poll is a backstop, not the primary trigger. The
 * trigger that matters is `visibilitychange`, which fires on precisely the
 * Android app-switch return that currently updates nothing. A short interval
 * would add request volume for a signal that only changes on deploy.
 */
export const VERSION_POLL_MS = 30 * 60 * 1000;

/**
 * MIN_CHECK_GAP_MS bounds how often a check can actually run, regardless of how
 * many triggers fire. Without it, flicking between apps repeatedly (each switch
 * firing `visibilitychange`) would issue a request per switch.
 */
export const MIN_CHECK_GAP_MS = 60 * 1000;

/**
 * useVersionCheck reports whether the origin is serving a different build than
 * the one this session is running.
 *
 * Checks on mount, whenever the document becomes visible, and on a slow timer —
 * and never while hidden, because a backgrounded PWA polling on a throttled
 * timer is pure waste (the answer cannot be acted on until the user returns).
 */
export function useVersionCheck(): { updateAvailable: boolean; servedVersion: string | null } {
  const [servedVersion, setServedVersion] = useState<string | null>(null);
  const lastCheckedAt = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const check = useCallback((force = false) => {
    const now = Date.now();
    if (!force && now - lastCheckedAt.current < MIN_CHECK_GAP_MS) return;
    lastCheckedAt.current = now;

    // Supersede any in-flight check: a slow response from the previous trigger
    // must not overwrite a fresher answer.
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    void fetchServedVersion(ac.signal).then((v) => {
      if (ac.signal.aborted) return;
      // A failed check leaves the previous answer in place rather than clearing
      // it: once we know an update exists, a later offline blip must not retract
      // the banner.
      if (v !== null) setServedVersion(v);
    });
  }, []);

  useEffect(() => {
    check(true);

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, VERSION_POLL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
      abortRef.current?.abort();
    };
  }, [check]);

  return { updateAvailable: isStale(BUILD_VERSION, servedVersion), servedVersion };
}
